import api from './api';

export const authService = {
  // Login user
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);

    return response.data;
  },

  // Register user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Get user profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Update user profile
  updateProfile: async (userData) => {
    const response = await api.put('/auth/profile', userData);
    return response.data;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/password', passwordData);
    return response.data;
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/request-password-reset', { email });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  // Revoke refresh token cookie server-side (real device logout)
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

export const pushService = {
  getVapidPublicKey: async () => {
    const response = await api.get('/push/vapid-public-key');
    return response.data.publicKey;
  },

  subscribe: async (subscription) => {
    const response = await api.post('/push/subscribe', subscription.toJSON());
    return response.data;
  },

  unsubscribe: async (endpoint) => {
    const response = await api.post('/push/unsubscribe', { endpoint });
    return response.data;
  },
};

export const adminNotificationService = {
  send: async ({ title, body, url, target = 'all' }) => {
    const response = await api.post('/admin/notifications/send', { title, body, url, target });
    return response.data;
  },

  getHistory: async () => {
    const response = await api.get('/admin/notifications/history');
    return response.data;
  },
};

export const charactersService = {
  // Get all by user characters (with pagination)
  getMeCharacters: async (params = {}) => {
    const response = await api.get('/characters/me', { params });
    return response.data;
  },

  // Get ALL characters (for admin bulk operations)
  getAllCharacters: async (params = {}) => {
    const response = await api.get('/characters', { params });
    return response.data;
  },

  // Get character by ID
  getCharacter: async (id) => {
    const response = await api.get(`/characters/${id}`);
    return response.data;
  },

  // Create character
  createCharacter: async (characterData) => {
    const response = await api.post('/characters', characterData);
    return response.data;
  },

  // Update character
  updateCharacter: async (id, characterData) => {
    const response = await api.put(`/characters/${id}`, characterData);
    return response.data;
  },

  // Delete character
  deleteCharacter: async (id) => {
    const response = await api.delete(`/characters/${id}`);
    return response.data;
  },

  // Level up character
  levelUpCharacter: async (id) => {
    const response = await api.post(`/characters/${id}/level-up`);
    return response.data;
  },

  // Add item to character
  addItem: async (characterId, itemData) => {
    const response = await api.post(`/characters/${characterId}/items`, itemData);
    return response.data;
  },

  // Add relic to character
  addRelic: async (characterId, relicData) => {
    const response = await api.post(`/characters/${characterId}/relics`, relicData);
    return response.data;
  },

  // Complete quest
  completeQuest: async (characterId, questData) => {
    const response = await api.post(`/characters/${characterId}/quests`, questData);
    return response.data;
  },

  // Update character item (enhancement)
  updateCharacterItem: async (characterId, characterItemId, itemData) => {
    const response = await api.put(`/characters/${characterId}/items/${characterItemId}`, itemData);
    return response.data;
  },

  // Remove character item
  removeCharacterItem: async (characterId, characterItemId) => {
    const response = await api.delete(`/characters/${characterId}/items/${characterItemId}`);
    return response.data;
  },
};

export const eventsService = {
  // Get all events
  getEvents: async (params = {}) => {
    const response = await api.get('/events', { params });
    return response.data;
  },

  // Get event by ID
  getEvent: async (id) => {
    const response = await api.get(`/events/${id}`);

    return response.data;
  },

  // Create event
  createEvent: async (eventData) => {
    const response = await api.post('/events', eventData);
    return response.data;
  },

  // Update event
  updateEvent: async (id, eventData) => {
    const response = await api.put(`/events/${id}`, eventData);
    return response.data;
  },

  // Delete event
  deleteEvent: async (id) => {
    const response = await api.delete(`/events/${id}`);
    return response.data;
  },

  // Complete event (Admin only)
  completeEvent: async (id) => {
    const response = await api.put(`/events/${id}/complete`);
    return response.data;
  },

  // Cancel event (Admin only)
  cancelEvent: async (id, reason) => {
    const response = await api.put(`/events/${id}/cancel`, { reason });
    return response.data;
  },

  // Participate in event
  participateInEvent: async (eventId, participationData) => {
    const response = await api.post(`/events/${eventId}/participate`, participationData);
    return response.data;
  },

  // Participate with multiple characters
  participateMultipleCharacters: async (eventId, participations) => {
    const response = await api.post(`/events/${eventId}/participate-multiple`, { participations });
    return response.data;
  },

  // Bulk confirm participants (Admin only)
  bulkConfirmParticipants: async (eventId, characterIds) => {
    const response = await api.post(`/events/${eventId}/bulk-confirm`, { characterIds });
    return response.data;
  },

  // Mark event participation
  markParticipation: async (eventId, participationData) => {
    const response = await api.post(`/events/${eventId}/mark-participation`, participationData);
    return response.data;
  },

  // Update participation pilot
  updateParticipationPilot: async (participationId, pilot) => {
    const response = await api.put(`/events/participation/${participationId}/pilot`, { pilot });
    return response.data;
  },

  // Admin: Confirm participation without proof
  adminConfirmParticipation: async (participationId) => {
    const response = await api.put(`/events/participation/${participationId}/admin-confirm`);
    return response.data;
  },


  // Admin: Toggle lent reported status
  updateLentReported: async (participationId, lentReported) => {
    const response = await api.patch(`/events/participation/${participationId}/lent-reported`, { lentReported });
    return response.data;
  },

  // Get user event history
  getUserEventHistory: async (userId = null) => {
    const url = userId ? `/events/user/${userId}/history` : '/events/user/history';
    const response = await api.get(url);
    return response.data;
  },

  // Submit proof of participation
  submitProof: async (eventId, proofData) => {
    const response = await api.post(`/events/${eventId}/proof`, proofData);
    return response.data;
  },

  // Get participation details
  getParticipation: async (eventId) => {
    const response = await api.get(`/events/${eventId}/participation`);
    return response.data;
  },

  // Admin: Get pending proofs
  getPendingProofs: async () => {
    const response = await api.get('/events/pending-proofs');
    return response.data;
  },

  // Admin: Review proof
  reviewProof: async (participationId, reviewData) => {
    const response = await api.put(`/events/participation/${participationId}/review`, reviewData);
    return response.data;
  },

  // Get gamification stats
  getGamificationStats: async () => {
    const response = await api.get('/events/gamification/stats');
    return response.data;
  },

  // Upload proof image
  uploadProof: async (formData) => {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Admin: Get all events with participations
  getAllEvents: async () => {
    const response = await api.get('/admin/events/all');
    return response.data;
  },

  // Admin: Get declined participations
  getDeclinedParticipations: async () => {
    const response = await api.get('/admin/events/declined-participations');
    return response.data;
  },

  // Admin: Get non-participants for an event
  getNonParticipants: async (eventId) => {
    const response = await api.get(`/events/${eventId}/non-participants`);
    return response.data;
  },
};

export const gamificationService = {
  // Get ranking
  getRanking: async (params = {}) => {
    const response = await api.get('/gamification/ranking', { params });
    return response.data;
  },

  // Get ranking level
  getRankingLevel: async (params = {}) => {
    const response = await api.get('/gamification/ranking/level', { params });
    return response.data;
  },

  // Get character position
  getCharacterPosition: async (characterId) => {
    const response = await api.get(`/gamification/characters/${characterId}/position`);
    return response.data;
  },

  // Get points history
  getPointsHistory: async (characterId, params = {}) => {
    const response = await api.get(`/gamification/characters/${characterId}/history`, { params });
    return response.data;
  },

  // Get points rules
  getPointsRules: async () => {
    const response = await api.get('/gamification/rules');
    return response.data;
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    const response = await api.get('/gamification/dashboard');
    return response.data;
  },

  // Get recent activities
  getRecentActivities: async (params = {}) => {
    const response = await api.get('/gamification/activities', { params });
    return response.data;
  },

  // Get user statistics
  getUserStats: async () => {
    const response = await api.get('/gamification/user-stats');
    return response.data;
  },

  // Admin: Create points rule
  createPointsRule: async (ruleData) => {
    const response = await api.post('/gamification/rules', ruleData);
    return response.data;
  },

  // Admin: Update points rule
  updatePointsRule: async (ruleData) => {
    const response = await api.put('/gamification/rules', ruleData);
    return response.data;
  },

  // Admin: Delete points rule
  deletePointsRule: async (action) => {
    const response = await api.delete(`/gamification/rules/${action}`);
    return response.data;
  },

  // Admin: Award custom points
  awardCustomPoints: async (pointsData) => {
    const response = await api.post('/gamification/award-points', pointsData);
    return response.data;
  },
};

export const adminService = {
  // Items
  getItems: async (params = {
    page: 1,
    limit: 200,
  }) => {
    const response = await api.get('/admin/items', { params });
    return response.data;
  },

  createItem: async (itemData) => {
    const response = await api.post('/admin/items', itemData);
    return response.data;
  },

  updateItem: async (id, itemData) => {
    const response = await api.put(`/admin/items/${id}`, itemData);
    return response.data;
  },

  deleteItem: async (id) => {
    const response = await api.delete(`/admin/items/${id}`);
    return response.data;
  },

  // Relics
  getRelics: async (params = {}) => {
    const response = await api.get('/admin/relics', { params });
    return response.data;
  },

  createRelic: async (relicData) => {
    const response = await api.post('/admin/relics', relicData);
    return response.data;
  },

  updateRelic: async (id, relicData) => {
    const response = await api.put(`/admin/relics/${id}`, relicData);
    return response.data;
  },

  deleteRelic: async (id) => {
    const response = await api.delete(`/admin/relics/${id}`);
    return response.data;
  },

  // Quests
  getQuests: async (params = {}) => {
    const response = await api.get('/admin/quests', { params });
    return response.data;
  },

  getQuestsReport: async (params = {}) => {
    const response = await api.get('/admin/quests-report', { params });
    return response.data;
  },

  createQuest: async (questData) => {
    const response = await api.post('/admin/quests', questData);
    return response.data;
  },

  updateQuest: async (id, questData) => {
    const response = await api.put(`/admin/quests/${id}`, questData);
    return response.data;
  },

  deleteQuest: async (id) => {
    const response = await api.delete(`/admin/quests/${id}`);
    return response.data;
  },

  // Users - Alistamento System
  getPendingUsers: async (params = {}) => {
    const response = await api.get('/admin/users/pending', { params });
    return response.data;
  },

  getAllUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId, rejectionReason) => {
    const response = await api.put(`/admin/users/${userId}/reject`, {
      rejectionReason
    });
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },

  toggleUserActive: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/toggle-active`);
    return response.data;
  },

  suspendUser: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/suspend`);
    return response.data;
  },

  startRecruitment: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/start-recruitment`);
    return response.data;
  },

  updateUserRole: async (id, roleData) => {
    const response = await api.put(`/admin/users/${id}/role`, roleData);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  // System stats
  getSystemStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Reports
  getEventParticipationReport: async (startDate, onlyApproved = true) => {
    const response = await api.get('/admin/reports/event-participation', {
      params: { startDate, onlyApproved }
    });
    return response.data;
  },
};

export const recapService = {
  // Get user's characters for recap
  getUserCharacters: async (year = 2025) => {
    const response = await api.get('/recap/characters', { params: { year } });
    return response.data;
  },

  // Get character recap for specific year
  getCharacterRecap: async (characterId, year = 2025) => {
    const response = await api.get(`/recap/${characterId}`, { params: { year } });
    return response.data;
  },
};

// Special Events Service
export { default as specialEventsService } from './specialEvents.service';

export const blogService = {
  // Posts
  getPosts: async (params = {}) => {
    const response = await api.get('/blog', { params });
    return response.data;
  },
  getPost: async (id) => {
    const response = await api.get(`/blog/${id}`);
    return response.data;
  },
  createPost: async (data) => {
    const response = await api.post('/blog', data);
    return response.data;
  },
  updatePost: async (id, data) => {
    const response = await api.put(`/blog/${id}`, data);
    return response.data;
  },
  deletePost: async (id) => {
    const response = await api.delete(`/blog/${id}`);
    return response.data;
  },

  // Categories
  getCategories: async () => {
    const response = await api.get('/blog/categories');
    return response.data;
  },
  createCategory: async (data) => {
    const response = await api.post('/blog/categories', data);
    return response.data;
  },
  updateCategory: async (id, data) => {
    const response = await api.put(`/blog/categories/${id}`, data);
    return response.data;
  },
  deleteCategory: async (id) => {
    const response = await api.delete(`/blog/categories/${id}`);
    return response.data;
  },
};
